/**
 * Mock data from MULTIPLE workflows
 * Demonstrates schema-driven approach
 */

import type { DashboardInsight, WorkflowManifest } from './types'

// Workflow Manifests - Both Emergence and Customer
export const WORKFLOW_MANIFESTS: WorkflowManifest[] = [
  {
    workflowId: 'wf-yield-exc',
    workflowName: 'Yield Excursion',
    workflowCategory: 'yield',
    isCustomerBuilt: false,
    generatedInsightTypes: ['yield_excursion', 'negative_yield_trend', 'bin_creep'],
    generatedMetricTypes: ['open_excursions', 'lots_impacted', 'wafers_impacted'],
    recommendedWidgets: ['insights_needing_attention', 'high_severity_items', 'open_action_queue'],
  },
  {
    workflowId: 'wf-process',
    workflowName: 'Yield Process Insights',
    workflowCategory: 'process',
    isCustomerBuilt: false,
    generatedInsightTypes: ['wat_correlation', 'process_contributor'],
    generatedMetricTypes: ['drifting_parameters', 'process_signals'],
    recommendedWidgets: ['insights_needing_attention', 'metric_trend'],
  },
  {
    workflowId: 'wf-npi',
    workflowName: 'NPI Analysis',
    workflowCategory: 'npi',
    isCustomerBuilt: false,
    generatedInsightTypes: ['coverage_gap', 'failing_pattern'],
    generatedMetricTypes: ['open_blockers', 'pattern_groups'],
    recommendedWidgets: ['insights_needing_attention', 'open_action_queue'],
  },
  // Customer workflows
  {
    workflowId: 'wf-trim',
    workflowName: 'Trim Stability Analysis',
    workflowCategory: 'custom',
    isCustomerBuilt: true,
    generatedInsightTypes: ['trim_drift_detected', 'trim_outlier_lot'],
    generatedMetricTypes: ['lots_with_trim_drift', 'trim_yield_impact'],
    recommendedWidgets: ['insights_needing_attention', 'open_action_queue'],
  },
  {
    workflowId: 'wf-package',
    workflowName: 'Package Reliability',
    workflowCategory: 'reliability',
    isCustomerBuilt: true,
    generatedInsightTypes: ['package_reliability_risk', 'field_return_correlation'],
    generatedMetricTypes: ['at_risk_shipments', 'customer_exposure'],
    recommendedWidgets: ['high_severity_items', 'open_action_queue'],
  },
]

// Insights from MULTIPLE workflows
export function getMultiWorkflowInsights(): DashboardInsight[] {
  return [
    // From Yield Excursion workflow
    {
      insightId: 'INS-YLD-001',
      title: 'Yield excursion detected on Excalibur-R-2600',
      shortSummary: 'Yield dropped from 96.3% to 86.8%. Affecting 14 lots and 392 wafers.',
      insightType: 'yield_excursion',
      workflowSource: 'Yield Excursion',
      workflowCategory: 'yield',
      severity: 'high',
      status: 'pending_review',
      affectedProducts: ['Excalibur-R-2600'],
      businessUnit: 'Mobile',
      technologyNode: '28nm',
      foundry: 'TSMC',
      testStage: 'Wafer Sort',
      lotsImpacted: 14,
      wafersImpacted: 392,
      generatedFromRunId: 'RUN-YLD-001',
      linkedReport: 'RPT-YLD-001',
      sourceDataScope: 'Product: Excalibur-R-2600, 28nm, TSMC',
      createdAt: '2026-04-29T14:30:00Z',
      lastUpdatedAt: '2026-04-30T09:15:00Z',
    },
    // From Process Insights workflow
    {
      insightId: 'INS-PROC-001',
      title: 'WAT parameter VTH_N drifting on 28nm node',
      shortSummary: 'VTH_N threshold voltage showing 8% drift. Correlated with low-yield lots.',
      insightType: 'wat_correlation',
      workflowSource: 'Yield Process Insights',
      workflowCategory: 'process',
      severity: 'high',
      status: 'under_investigation',
      affectedProducts: ['Excalibur-R-2600', 'Excalibur-X-9000'],
      businessUnit: 'Mobile',
      technologyNode: '28nm',
      foundry: 'TSMC',
      testStage: 'Wafer Sort',
      lotsImpacted: 8,
      wafersImpacted: 224,
      generatedFromRunId: 'RUN-PROC-001',
      linkedReport: 'RPT-PROC-001',
      sourceDataScope: 'Node: 28nm, TSMC',
      createdAt: '2026-04-28T11:20:00Z',
      lastUpdatedAt: '2026-04-29T16:45:00Z',
    },
    // From Customer Trim Stability workflow
    {
      insightId: 'INS-TRIM-001',
      title: 'Trim drift detected on Power Management IC-500',
      shortSummary: 'Fine trim stage showing 5% drift on VREF parameter. 3 lots affected.',
      insightType: 'trim_drift_detected',
      workflowSource: 'Trim Stability Analysis',
      workflowCategory: 'custom',
      severity: 'medium',
      status: 'pending_review',
      affectedProducts: ['Power Management IC-500'],
      businessUnit: 'Automotive',
      technologyNode: '28nm',
      foundry: 'TSMC',
      testStage: 'Final Test',
      lotsImpacted: 3,
      wafersImpacted: 84,
      generatedFromRunId: 'RUN-TRIM-001',
      linkedReport: 'RPT-TRIM-001',
      linkedArtifact: 'outlier-lot-list-401',
      sourceDataScope: 'Product: Power Management IC-500, Trim Stage: Fine Trim',
      createdAt: '2026-04-29T07:45:00Z',
      lastUpdatedAt: '2026-04-30T09:30:00Z',
    },
    // From Customer Package Reliability workflow
    {
      insightId: 'INS-PKG-001',
      title: 'Package reliability risk on RF Transceiver Gen4',
      shortSummary: 'Elevated field return correlation detected. 12 field failures with common signature.',
      insightType: 'package_reliability_risk',
      workflowSource: 'Package Reliability',
      workflowCategory: 'reliability',
      severity: 'high',
      status: 'pending_review',
      affectedProducts: ['RF Transceiver Gen4'],
      businessUnit: 'Mobile',
      technologyNode: '14nm',
      foundry: 'GlobalFoundries',
      testStage: 'Final Test',
      lotsImpacted: 2,
      wafersImpacted: 56,
      customerImpact: 'Customer: ZephyrTech, 12 field failures',
      generatedFromRunId: 'RUN-PKG-001',
      linkedReport: 'RPT-PKG-001',
      linkedArtifact: 'at-risk-lot-list-301',
      sourceDataScope: 'Product: RF Transceiver Gen4, Customer: ZephyrTech',
      createdAt: '2026-04-28T14:20:00Z',
      lastUpdatedAt: '2026-04-30T10:15:00Z',
    },
    // From NPI Analysis workflow
    {
      insightId: 'INS-NPI-001',
      title: 'Coverage gap on Sensor Hub Pro',
      shortSummary: 'Test pattern coverage at 78%. Memory BIST patterns missing.',
      insightType: 'coverage_gap',
      workflowSource: 'NPI Analysis',
      workflowCategory: 'npi',
      severity: 'medium',
      status: 'new',
      affectedProducts: ['Sensor Hub Pro'],
      businessUnit: 'IoT',
      technologyNode: '16nm',
      foundry: 'TSMC',
      testStage: 'Wafer Sort',
      lotsImpacted: 3,
      wafersImpacted: 84,
      generatedFromRunId: 'RUN-NPI-001',
      linkedReport: 'RPT-NPI-001',
      sourceDataScope: 'Product: Sensor Hub Pro, Stage: NPI',
      createdAt: '2026-04-30T06:00:00Z',
      lastUpdatedAt: '2026-04-30T06:00:00Z',
    },
  ]
}

// Helper to filter insights by dashboard scope
export function filterInsightsByScope(
  insights: DashboardInsight[],
  scope: {
    products?: string[]
    nodes?: string[]
    businessUnits?: string[]
    foundries?: string[]
    workflows?: string[]
    workflowCategories?: string[]
    severity?: string[]
    status?: string[]
  },
): DashboardInsight[] {
  return insights.filter((insight) => {
    // Product filter
    if (scope.products && scope.products.length > 0) {
      if (!insight.affectedProducts.some((p) => scope.products!.includes(p))) {
        return false
      }
    }

    // Node filter
    if (scope.nodes && scope.nodes.length > 0) {
      if (!scope.nodes.includes(insight.technologyNode)) {
        return false
      }
    }

    // Business Unit filter
    if (scope.businessUnits && scope.businessUnits.length > 0) {
      if (!scope.businessUnits.includes(insight.businessUnit)) {
        return false
      }
    }

    // Foundry filter
    if (scope.foundries && scope.foundries.length > 0) {
      if (!scope.foundries.includes(insight.foundry)) {
        return false
      }
    }

    // Workflow filter - KEY FEATURE!
    if (scope.workflows && scope.workflows.length > 0) {
      if (!scope.workflows.includes(insight.workflowSource)) {
        return false
      }
    }

    // Workflow Category filter
    if (scope.workflowCategories && scope.workflowCategories.length > 0) {
      if (!scope.workflowCategories.includes(insight.workflowCategory)) {
        return false
      }
    }

    // Severity filter
    if (scope.severity && scope.severity.length > 0) {
      if (!scope.severity.includes(insight.severity)) {
        return false
      }
    }

    // Status filter
    if (scope.status && scope.status.length > 0) {
      if (!scope.status.includes(insight.status)) {
        return false
      }
    }

    return true
  })
}

export type WidgetType =
  | 'insights_needing_attention'
  | 'high_severity_items'
  | 'open_action_queue'
  | 'impacted_business_objects'
  | 'recent_workflow_findings'
  | 'workflow_coverage'
  | 'metric_trend'

export type PersonaTemplate =
  | 'product_engineer'
  | 'process_engineer'
  | 'engineering_lead'
  | 'test_engineer'
  | 'senior_executive'
